/* Owns the failed-query state. It renders `t('errors.' + code)` so the message
   the user sees is the panel's own translation of the frozen `ErrorCode`, never
   the API's log-oriented `message` field. The `reqId` is shown in small mono so
   a support ticket can quote it. */

import type { ErrorCode } from '@sentinelac/shared'
import { AlertTriangle } from 'lucide-react'
import type { ReactNode } from 'react'

import { useT } from '../../i18n/index.js'
import { Button } from './Button.js'
import { cn } from './cn.js'

export interface ErrorStateProps {
  code?: ErrorCode
  title?: string
  description?: string
  reqId?: string
  onRetry?: () => void
  compact?: boolean
  className?: string
  'data-testid'?: string
}

export function ErrorState({
  code,
  title,
  description,
  reqId,
  onRetry,
  compact = false,
  className,
  'data-testid': testId,
}: ErrorStateProps): ReactNode {
  const t = useT()
  const heading = title ?? (code ? t(`errors.${code}`) : t('common.errorTitle'))

  return (
    <div
      role="alert"
      data-testid={testId}
      className={cn(
        'flex flex-col items-center gap-2 rounded-lg border border-danger/40 bg-panel text-center',
        compact ? 'px-4 py-4' : 'px-6 py-10',
        className,
      )}
    >
      <AlertTriangle aria-hidden className={cn('text-danger', compact ? 'size-5' : 'size-8')} />
      <h3 className={cn('font-medium text-text', compact ? 'text-sm' : 'text-lg')}>{heading}</h3>
      {description ? <p className="max-w-prose text-sm text-text-muted">{description}</p> : null}
      {reqId ? (
        <p className="font-mono text-xs text-text-faint select-all">
          {t('common.requestId')}: {reqId}
        </p>
      ) : null}
      {onRetry ? (
        <Button variant="secondary" size={compact ? 'sm' : 'md'} onClick={onRetry} className="mt-1">
          {t('common.retry')}
        </Button>
      ) : null}
    </div>
  )
}
